import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to manage tutorial state and completion tracking
 * Similar to iOS TutorialManager.swift
 */
export function useTutorial(tutorialType = 'firstTime') {
  const { user } = useAuth();
  const [showTutorial, setShowTutorial] = useState(false);
  const [loading, setLoading] = useState(true);

  // Field name in Firestore based on tutorial type
  const completionField = tutorialType === 'firstTime' 
    ? 'hasCompletedFirstTutorial' 
    : `hasCompleted${tutorialType.charAt(0).toUpperCase() + tutorialType.slice(1)}Tutorial`;

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    checkTutorialStatus();
  }, [user?.uid, tutorialType]);

  const checkTutorialStatus = async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const hasCompleted = userData[completionField] || false;
        // Never auto-start; the tutorial is only triggered via the forceShow button
        setShowTutorial(false);
        void hasCompleted;
      } else {
        setShowTutorial(false);
      }
    } catch (error) {
      console.error('❌ Error checking tutorial status:', error);
      setShowTutorial(false);
    } finally {
      setLoading(false);
    }
  };

  const markTutorialCompleted = async () => {
    if (!user?.uid) return;

    try {
      await setDoc(
        doc(db, 'users', user.uid),
        { [completionField]: true },
        { merge: true }
      );
      console.log(`✅ Tutorial marked as completed: ${completionField}`);
      setShowTutorial(false);
    } catch (error) {
      console.error(`❌ Error marking tutorial complete: ${error.message}`);
    }
  };

  return {
    showTutorial,
    loading,
    markTutorialCompleted,
    checkTutorialStatus
  };
}

